import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "./ui/hover-card";
import { Badge } from "./ui/badge";
import { Info, DollarSign, Scale, Shield, Users, Settings } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cache for impact definitions
let definitionsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const CATEGORY_ICONS = {
  financial: DollarSign,
  legal: Scale,
  regulatory: Shield,
  reputational: Users,
  operational: Settings,
};

const CATEGORY_COLORS = {
  financial: "text-emerald-600",
  legal: "text-blue-600",
  regulatory: "text-purple-600",
  reputational: "text-amber-600",
  operational: "text-red-600",
};

const LEVEL_COLORS = {
  high: "bg-red-100 text-red-700 border-red-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-emerald-100 text-emerald-700 border-emerald-200",
  none: "bg-slate-100 text-slate-600 border-slate-200",
  no_impact: "bg-slate-100 text-slate-600 border-slate-200",
};

// Hook to fetch and cache definitions
export function useImpactDefinitions() {
  const { token } = useAuth();
  const [definitions, setDefinitions] = useState(definitionsCache);
  const [loading, setLoading] = useState(!definitionsCache);

  const fetchDefinitions = useCallback(async () => {
    // Check cache
    if (definitionsCache && cacheTimestamp && (Date.now() - cacheTimestamp) < CACHE_DURATION) {
      setDefinitions(definitionsCache);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/impact-definitions/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        definitionsCache = data.definitions;
        cacheTimestamp = Date.now();
        setDefinitions(data.definitions);
      }
    } catch (error) {
      console.error("Error fetching impact definitions:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchDefinitions();
    }
  }, [token, fetchDefinitions]);

  return { definitions, loading, refetch: fetchDefinitions };
}

// Simple tooltip showing level definition
export function ImpactLevelTooltip({ category, level, children }) {
  const { definitions, loading } = useImpactDefinitions();
  
  if (loading || !definitions) {
    return children;
  }

  const categoryData = definitions[category];
  const levelKey = level === "no_impact" ? "none" : level;
  const levelData = categoryData?.[levelKey];
  
  if (!levelData) {
    return children;
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          {children}
        </TooltipTrigger>
        <TooltipContent className="max-w-xs p-3" side="top">
          <div className="space-y-2">
            <div className="font-medium">{levelData.title}</div>
            <p className="text-sm text-muted-foreground">{levelData.description}</p>
            {levelData.examples?.length > 0 && (
              <ul className="text-xs space-y-1 border-t pt-2 mt-2">
                {levelData.examples.slice(0, 3).map((ex, i) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="text-muted-foreground">•</span>
                    <span>{ex}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Hover card showing full category definition
export function ImpactCategoryHoverCard({ category, children }) {
  const { definitions, loading } = useImpactDefinitions();
  
  if (loading || !definitions) {
    return children;
  }

  const categoryData = definitions[category];
  const Icon = CATEGORY_ICONS[category] || Info;
  const color = CATEGORY_COLORS[category] || "text-slate-600";
  
  if (!categoryData) {
    return children;
  }

  return (
    <HoverCard openDelay={200}>
      <HoverCardTrigger asChild>
        {children}
      </HoverCardTrigger>
      <HoverCardContent className="w-[500px] p-4 max-h-[80vh] overflow-y-auto" side="right" align="start">
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b">
            <Icon className={`h-6 w-6 ${color}`} />
            <h4 className="font-bold text-lg">{categoryData.category_name || category}</h4>
          </div>
          
          <div className="space-y-3">
            {["high", "medium", "low", "none"].map((level) => {
              const levelData = categoryData[level];
              if (!levelData) return null;
              
              return (
                <div key={level} className={`p-3 rounded-lg border-2 ${LEVEL_COLORS[level]}`}>
                  <div className="font-semibold mb-2">{levelData.title || level.toUpperCase()}</div>
                  <p className="text-sm mb-2">{levelData.description}</p>
                  {levelData.examples?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-current/20">
                      <p className="text-xs font-medium mb-1">Examples:</p>
                      <ul className="text-xs space-y-1">
                        {levelData.examples.map((ex, i) => (
                          <li key={i} className="flex items-start gap-1">
                            <span className="opacity-60">•</span>
                            <span className="opacity-90">{ex}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {levelData.thresholds && Object.keys(levelData.thresholds).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-current/20">
                      <p className="text-xs font-medium mb-1">Thresholds:</p>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(levelData.thresholds).map(([key, value]) => (
                          <Badge key={key} variant="outline" className="text-xs">
                            {key.replace(/_/g, " ")}: {typeof value === "number" ? value.toLocaleString() : value}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

// Info button that shows definition in a popover
export function ImpactInfoButton({ category, level, className = "" }) {
  const { definitions, loading } = useImpactDefinitions();
  
  if (loading || !definitions) {
    return null;
  }

  const categoryData = definitions[category];
  const levelKey = level === "no_impact" ? "none" : level;
  const levelData = categoryData?.[levelKey];
  
  if (!levelData) {
    return null;
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <button className={`p-1 hover:bg-muted rounded-full transition-colors ${className}`} type="button">
            <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-md p-4" side="right" sideOffset={10}>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Badge className={LEVEL_COLORS[levelKey]}>{levelData.title}</Badge>
            </div>
            <p className="text-sm leading-relaxed">{levelData.description}</p>
            {levelData.examples?.length > 0 && (
              <div className="border-t pt-3 mt-3">
                <p className="text-xs font-semibold mb-2">Examples:</p>
                <ul className="text-sm space-y-1.5">
                  {levelData.examples.map((ex, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>{ex}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {levelData.thresholds && Object.keys(levelData.thresholds).length > 0 && (
              <div className="border-t pt-3 mt-3">
                <p className="text-xs font-semibold mb-2">Thresholds:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(levelData.thresholds).map(([key, value]) => (
                    <Badge key={key} variant="secondary" className="text-xs">
                      {key.replace(/_/g, " ")}: {typeof value === "number" ? value.toLocaleString() : value}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Side panel showing all definitions for a category
export function ImpactDefinitionPanel({ category, selectedLevel, onClose }) {
  const { definitions, loading } = useImpactDefinitions();
  
  if (loading || !definitions) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Loading definitions...
      </div>
    );
  }

  const categoryData = definitions[category];
  const Icon = CATEGORY_ICONS[category] || Info;
  const color = CATEGORY_COLORS[category] || "text-slate-600";
  
  if (!categoryData) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        No definitions available for this category.
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={`h-5 w-5 ${color}`} />
          <h3 className="font-semibold">{categoryData.category_name || category}</h3>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            ✕
          </button>
        )}
      </div>
      
      <div className="space-y-3">
        {["high", "medium", "low", "none"].map((level) => {
          const levelData = categoryData[level];
          if (!levelData) return null;
          
          const isSelected = selectedLevel === level || (selectedLevel === "no_impact" && level === "none");
          
          return (
            <div 
              key={level} 
              className={`p-3 rounded-lg border-2 transition-all ${LEVEL_COLORS[level]} ${
                isSelected ? "ring-2 ring-primary ring-offset-2" : ""
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold">{levelData.title}</span>
                {isSelected && (
                  <Badge variant="outline" className="text-xs">Selected</Badge>
                )}
              </div>
              <p className="text-sm mb-2">{levelData.description}</p>
              {levelData.examples?.length > 0 && (
                <ul className="text-xs space-y-1">
                  {levelData.examples.map((ex, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <span className="opacity-60">•</span>
                      <span className="opacity-80">{ex}</span>
                    </li>
                  ))}
                </ul>
              )}
              {levelData.thresholds && Object.keys(levelData.thresholds).length > 0 && (
                <div className="mt-2 pt-2 border-t border-current/10">
                  <p className="text-xs font-medium mb-1">Thresholds:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(levelData.thresholds).map(([key, value]) => (
                      <Badge key={key} variant="outline" className="text-xs">
                        {key.replace(/_/g, " ")}: {typeof value === "number" ? value.toLocaleString() : value}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default {
  useImpactDefinitions,
  ImpactLevelTooltip,
  ImpactCategoryHoverCard,
  ImpactInfoButton,
  ImpactDefinitionPanel,
};
